import React, { useEffect, useState, Suspense, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { IoTelescope } from "react-icons/io5";
import { FiInfo } from 'react-icons/fi';
import { CiCircleQuestion } from "react-icons/ci";
import Swal from 'sweetalert2';
import Style from '../assets/css/planets.module.css';

const Model = ({ url, position, scale }) => {
    const [model, setModel] = useState();
    const [error, setError] = useState(null);

    useEffect(() => {
        const loader = new GLTFLoader();
        loader.load(
            url,
            (gltf) => {
                setModel(gltf.scene);
            },
            undefined,
            (err) => {
                console.error('Error loading model:', err);
                setError('Error loading model');
            }
        );
    }, [url]);

    useFrame(() => {
        if (model) {
            model.rotation.y += 0.005;
        }
    });

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <mesh position={position} scale={scale} >
            {model && <primitive object={model} />}
        </mesh>
    );
};

const Planets = () => {
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedPlanet, setSelectedPlanet] = useState(null);
    const [cameraDistance, setCameraDistance] = useState({ min: false, max: false });
    const [zoomLevel, setZoomLevel] = useState(1);
    const planetsPerPage = 8;
    const [filteredData, setFilteredData] = useState([]);
    const { t } = useTranslation();
    const detailsRef = useRef(null);

    const handlePlanetClick = (planet) => {
        setSelectedPlanet(planet);
        
        setTimeout(() => {
            detailsRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }, 100);
    };

    const getMaxZoomValue = (radius) => {
        if (radius < 3) return 50;
        if (radius < 5) return 25;
        if (radius < 10) return 10;
        if (radius < 15) return 7;
        if (radius < 20) return 5;
        if (radius < 32) return 3;
        return 1;
    };

    useEffect(() => {
        // Cargar el archivo JSON directamente
        fetch('PSCompPars.json') // Asegúrate de tener este archivo en tu directorio público
            .then(response => response.json())
            .then(jsonData => {
                // Procesar los datos como antes pero ahora vienen en formato JSON
                const processedData = jsonData.map(item => ({
                    pl_name: item.pl_name,
                    pl_rade: item.pl_rade,
                    pl_bmasse: item.pl_bmasse,
                    pl_orbeccen: item.pl_orbeccen,
                    discoverymethod: item.discoverymethod,
                    sy_gaiamag: item.sy_gaiamag,
                    pl_orbper: item.pl_orbper,
                    sy_dist: item.sy_dist ? parseFloat(item.sy_dist) : Infinity,
                    disc_year: item.disc_year,
                    hostname: item.hostname,
                    sy_snum: item.sy_snum,
                    sy_pnum: item.sy_pnum,
                    st_spectype: item.st_spectype,
                }));

                // Ordenar por distancia
                processedData.sort((a, b) => a.sy_dist - b.sy_dist);
                
                setData(processedData);
                setFilteredData(processedData);
            })
            .catch(error => {
                console.error('Error loading JSON data:', error);
            });
    }, []);

    // Resto del código permanece igual...
    const indexOfLastPlanet = currentPage * planetsPerPage;
    const indexOfFirstPlanet = indexOfLastPlanet - planetsPerPage;
    const currentPlanets = filteredData.slice(indexOfFirstPlanet, indexOfLastPlanet);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const totalPages = Math.ceil(filteredData.length / planetsPerPage);

    const generatePaginationButtons = () => {
        const buttons = [];
        let startPage = Math.max(currentPage - 2, 2);
        let endPage = Math.min(currentPage + 2, totalPages - 1);

        if (totalPages <= 5) {
            startPage = 1;
            endPage = totalPages;
        } else {
            if (currentPage >= totalPages - 2) {
                startPage = totalPages - 4;
            }
            if (currentPage <= 3) {
                endPage = 5;
            }
        }

        buttons.push(
            <button
                key={1}
                onClick={() => paginate(1)}
                className={currentPage === 1 ? Style.activePage : Style.pageButton}
            >
                1
            </button>
        );

        for (let i = startPage; i <= endPage; i++) {
            if (i !== 1 && i !== totalPages) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => paginate(i)}
                        className={currentPage === i ? Style.activePage : Style.pageButton}
                    >
                        {i}
                    </button>
                );
            }
        }

        if (totalPages > 1) {
            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => paginate(totalPages)}
                    className={currentPage === totalPages ? Style.activePage : Style.pageButton}
                >
                    {totalPages}
                </button>
            );
        }
        return buttons;
    };

    const handleZoomChange = (event) => setZoomLevel(Number(event.target.value));

    useEffect(() => {
        setZoomLevel(1);
    }, [selectedPlanet]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData]);

    const handleFilterChange = (query) => {
        if (!query) {
            setFilteredData(data);
            return;
        }

        let filtered = data.filter((item) =>
            item.disc_year?.toString().includes(query) ||
            item.discoverymethod?.toLowerCase().includes(query.toLowerCase()) ||
            item.pl_rade?.toString().includes(query) ||
            item.pl_name?.toLowerCase().includes(query.toLowerCase())
        );

        setFilteredData(filtered);
    };

    return (
        <>
            <div className={Style.tablePlanetsContainer}>
                <h1 className={Style.titlePlanets}>{t('data_planet')}</h1>
                <h2 className={Style.title2Planets}>5756 {t('exo')}</h2>
                <div className={Style.filterContainer}>
                    <span className={Style.spanContainer}>
                        <input
                            type="text"
                            placeholder={t('filterPlanets')}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className={Style.filterInput}
                        />
                        <IoTelescope />
                        <FiInfo
                            className={Style.infoPlanets}
                            onClick={() => Swal.fire({
                                icon: 'warning',
                                text: t('infoPlanets')
                            })}
                        />
                    </span>
                </div>
                <div className={Style.tableWrapper}>
                    <table className={Style.tablePlanets}>
                        <thead>
                            <tr>
                                <th>{t('planetName')}</th>
                                <th>{t('planetRadius')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetRadiusInfo') })} /></th>
                                <th>{t('planetMass')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetMassInfo') })} /></th>
                                <th>{t('planetEccentricity')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetEccentricityInfo') })} /></th>
                                <th>{t('planetGaiaMagnitude')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetGaiaInfo') })} /></th>
                                <th>{t('planetOrbitalPeriod')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetOrbitalInfo') })} /></th>
                                <th>{t('planetDistance')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetDistanceInfo') })} /></th>
                                <th>{t('planetDiscoveredMethod')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetMethodInfo') })} /></th>
                                <th>{t('planetDiscoveredYear')}<br /><CiCircleQuestion className={Style.infoPlanets} onClick={() => Swal.fire({ icon: 'question', text: t('planetYearInfo') })} /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPlanets.map((item, index) => (
                                <tr key={index}>
                                    <td>
                                        <button className={Style.buttonPlanet} onClick={() => handlePlanetClick(item)}>
                                            {item.pl_name || 'No data'}
                                            <svg className={Style.stars} viewBox="0 0 353 178" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M271 37.9914C289.244 33.3118 295.91 27.0769 299.991 9C305.137 29.7552 312.313 36.1206 331.104 40.1127C312.194 43.8462 305.081 49.4282 302.113 69.1041C296.925 50.845 289.057 44.9589 271 37.9914Z"></path>
                                                <path d="M254.852 83.012C245.919 73.8857 239.998 72.0853 228 75.817C238.443 65.759 239.748 59.3874 235.721 47C244.325 56.8385 250.014 59.1225 262.574 54.195C253.611 63.4315 252.822 70.0469 254.852 83.012Z"></path>
                                                <path d="M275.626 97.3875C278.179 88.2512 277.138 83.7734 270.281 77.4407C280.63 80.423 285.214 78.8959 291.687 71.7049C288.553 80.8937 289.197 85.4019 297.032 91.6517C287.761 89.3164 283.212 91.2658 275.626 97.3875Z"></path>
                                            </svg>
                                        </button>
                                    </td>
                                    <td>{item.pl_rade ? item.pl_rade : 'No data'}</td>
                                    <td>{item.pl_bmasse || 'No data'}</td>
                                    <td>{item.pl_orbeccen || 'No data'}</td>
                                    <td>{item.sy_gaiamag || 'No data'}</td>
                                    <td>{item.pl_orbper || 'No data'}</td>
                                    <td>{item.sy_dist || 'No data'}</td>
                                    <td>{item.discoverymethod || 'No data'}</td>
                                    <td>{item.disc_year || 'No data'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={Style.pagination}>
                    <button aria-label="Pages" onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)} disabled={currentPage === 1} className={Style.pageButton}>
                        <FaArrowLeft />
                    </button>
                    {generatePaginationButtons()}
                    <button aria-label="Pages" onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)} disabled={currentPage === totalPages} className={Style.pageButton}>
                        <FaArrowRight />
                    </button>
                </div>
            </div>
            {!selectedPlanet ? (
                <></>
            ) : (
                <div ref={detailsRef} className={Style.planetDetails}>
                    <h2>{t('planetDetails')} {selectedPlanet.pl_name}</h2>
                    <div className={Style.tableWrapper}>
                        <table className={Style.detailsTable}>
                            <thead>
                                <tr>
                                    <th>{t('planetHostStar')}</th>
                                    <th>{t('planetStarsNumber')}</th>
                                    <th>{t('planetPlanets')}</th>
                                    <th>{t('planetDiscoveredMethod')}</th>
                                    <th>{t('planetDiscoveredYear')}</th>
                                    <th>{t('planetOrbitalPeriod')}</th>
                                    <th>{t('planetRadius')}</th>
                                    <th>{t('planetClassification')}</th>
                                    <th>{t('planetDistance')}</th>
                                    <th>{t('planetGaiaMagnitude')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{selectedPlanet.hostname || 'No data'}</td>
                                    <td>{selectedPlanet.sy_snum || 'No data'}</td>
                                    <td>{selectedPlanet.sy_pnum || 'No data'}</td>
                                    <td>{selectedPlanet.discoverymethod || 'No data'}</td>
                                    <td>{selectedPlanet.disc_year || 'No data'}</td>
                                    <td>{selectedPlanet.pl_orbper || 'No data'}</td>
                                    <td>{selectedPlanet.pl_rade || 'No data'}</td>
                                    <td>{selectedPlanet.st_spectype || 'No data'}</td>
                                    <td>{selectedPlanet.sy_dist || 'No data'}</td>
                                    <td>{selectedPlanet.sy_gaiamag || 'No data'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className={Style.planetsComparisonContainer} style={{ display: selectedPlanet ? 'block' : 'none' }}>
                <div className={Style.comparingPlanets}>
                    <h1 className={Style.titlePlanetsComparison}>{t('planetComparison')}</h1>
                    <input
                        type="range"
                        min="1"
                        max={selectedPlanet?.pl_rade ? getMaxZoomValue(selectedPlanet.pl_rade) : 30}
                        value={zoomLevel}
                        onChange={handleZoomChange}
                        className={Style.zoomInput}
                        aria-label="Zoom level"
                    />
                    <div className={Style.canvasContainer}>
                        <Suspense fallback={<div>Cargando modelo...</div>}>
                            <Canvas className={Style.glassComparison}>
                                <ambientLight intensity={1} />
                                <pointLight position={[-1, 1, 1]} />
                                <OrbitControls enablePan={false} enableZoom={false} enableRotate={false} />
                                {selectedPlanet && selectedPlanet.pl_rade ? (
                                    <Model url="/Models/kepler/scene.gltf" position={[0, 0, 0]} scale={zoomLevel * (selectedPlanet.pl_rade * 0.0051)} />
                                ) : (
                                    <></>
                                )}
                            </Canvas>
                        </Suspense>
                        <Suspense fallback={<div>Cargando modelo...</div>}>
                            <Canvas className={Style.glassComparison}>
                                <ambientLight intensity={1} />
                                <pointLight position={[-1, 1, 1]} />
                                <OrbitControls enablePan={false} enableZoom={false} enableRotate={false} />
                                <Model url="/Models/earth/scene.gltf" position={[0, 0, 0]} scale={zoomLevel * (0.0001)} />
                            </Canvas>
                        </Suspense>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Planets;
